import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, CalendarIcon, Clock, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useLocationExtraction } from '@/hooks/useLocationExtraction';
import { usePostProcessing } from '@/hooks/usePostProcessing';
import PostPreviewHeader from '@/components/Posts/PostPreviewHeader';
import PostPreviewNotice from '@/components/Posts/PostPreviewNotice';
import PostMetadata from '@/components/Posts/PostMetadata';
import PostContent from '@/components/Posts/PostContent';
import SEOInfoCard from '@/components/Posts/SEOInfoCard';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string | null;
  featured_image: string | null;
  slug: string;
  author: string;
  published_date: string | null;
  status: 'draft' | 'published';
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string[] | null;
  categories: string[] | null;
  tags: string[] | null;
  created_at: string | null;
  updated_at: string | null;
}

const PostPreview = () => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const { locationInfo, setLocationInfo, extractLocationInfo } = useLocationExtraction();
  const { processedPost, setProcessedPost, processLocationVariables, calculateReadTime } = usePostProcessing();

  useEffect(() => {
    if (id) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      const blogPost = data as BlogPost;
      setPost(blogPost);
      
      // Extract location info and process variables
      const extractedLocation = extractLocationInfo(blogPost);
      console.log('Extracted location:', extractedLocation);
      setLocationInfo(extractedLocation);
      
      if (extractedLocation) {
        await processLocationVariables(blogPost, extractedLocation);
      } else {
        // For template posts, keep original variables as-is
        setProcessedPost(blogPost);
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      toast.error('Failed to load blog post');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto py-16 px-4">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!post || !processedPost) {
    return (
      <Layout>
        <div className="container mx-auto py-16 px-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
            <p className="text-muted-foreground mb-6">The blog post you're looking for doesn't exist.</p>
            <Button asChild>
              <Link to="/posts">Back to Posts</Link>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const readTime = calculateReadTime(processedPost.content);

  return (
    <Layout>
      <div className="container mx-auto py-8 px-4">
        <PostPreviewHeader 
          postStatus={post.status} 
          locationInfo={locationInfo} 
        />

        <PostPreviewNotice locationInfo={locationInfo} />

        <PostMetadata 
          author={processedPost.author}
          publishedDate={processedPost.published_date}
          readTime={readTime}
        />

        <PostContent 
          post={processedPost}
          readTime={readTime}
        />

        <SEOInfoCard 
          seoTitle={processedPost.seo_title}
          seoDescription={processedPost.seo_description}
          seoKeywords={processedPost.seo_keywords}
        />
      </div>
    </Layout>
  );
};

export default PostPreview;
